export const DEFAULT_LANGUAGE = "pt-BR";
export const FALLBACK_LANGUAGE = "en-US";
export const SUPPORTED_LANGUAGES = [
    DEFAULT_LANGUAGE,
    FALLBACK_LANGUAGE
] as const;
export const PREFERRED_LANGUAGE_STORAGE_KEY = "CompleoSelectedLanguage";
export const I18N_PATH_STORAGE_KEY = "i18nPath";

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const normalizeSupportedLanguage = (
    language?: string | null
): SupportedLanguage | null => {
    const normalizedLanguage = String(language || "").trim();

    if (!normalizedLanguage) {
        return null;
    }

    const lowerCasedLanguage = normalizedLanguage.toLowerCase();

    if (lowerCasedLanguage.startsWith("pt")) {
        return DEFAULT_LANGUAGE;
    }

    if (lowerCasedLanguage.startsWith("en")) {
        return FALLBACK_LANGUAGE;
    }

    if (SUPPORTED_LANGUAGES.includes(normalizedLanguage as SupportedLanguage)) {
        return normalizedLanguage as SupportedLanguage;
    }

    return null;
};

const getLocalStorageItem = (key: string) => {
    try {
        return globalThis.localStorage?.getItem(key) || null;
    } catch {
        return null;
    }
};

const setLocalStorageItem = (key: string, value: string) => {
    try {
        globalThis.localStorage?.setItem(key, value);
    } catch {
        return;
    }
};

export const getStoredI18nPath = () =>
    getLocalStorageItem(I18N_PATH_STORAGE_KEY);

export const setStoredI18nPath = (path: string) => {
    setLocalStorageItem(I18N_PATH_STORAGE_KEY, path);
};

export const getStoredPreferredLanguage = () =>
    normalizeSupportedLanguage(
        getLocalStorageItem(PREFERRED_LANGUAGE_STORAGE_KEY)
    );

export const clearStoredPreferredLanguage = () => {
    try {
        globalThis.localStorage?.removeItem(PREFERRED_LANGUAGE_STORAGE_KEY);
    } catch {
        return;
    }
};

export const persistPreferredLanguage = (language?: string | null) => {
    const normalizedLanguage = normalizeSupportedLanguage(language);

    if (normalizedLanguage) {
        setLocalStorageItem(PREFERRED_LANGUAGE_STORAGE_KEY, normalizedLanguage);
    }

    return normalizedLanguage;
};

export const getPreferredLanguage = (currentLanguage?: string | null) => {
    return (
        getStoredPreferredLanguage() ||
        normalizeSupportedLanguage(currentLanguage) ||
        DEFAULT_LANGUAGE
    );
};
